import { patchExactReceiptCounter } from "@/api/receipt-counter/receipt-counter";
import { dexieDB, IReceiptCounterPayload } from "@/services/dexie-database";
import { LogLevel, LogService } from "@/services/logging";
import ReceiptModule from "@/store/modules/receipt";
import { CounterStatus } from "@/types/db/transaction";
import { SyncStatus } from "@/types/db/product-slot-booking";

/**
 * Sync all pending receipt counter payloads from Dexie to backend.
 * Deletes the record from Dexie on success.
 * Also cleans up stale records where the value is less than the current state counter.
 */
export async function syncDexieReceiptCounters(): Promise<void> {
  if (!dexieDB.isOpen()) {
    LogService.info("ReceiptCounter Sync: Database not open, skipping");
    return;
  }

  const payloads = await dexieDB.receiptCounterPayload
    .where("syncStatus")
    .equals(SyncStatus.Pending)
    .toArray();

  if (!payloads.length) {
    return;
  }

  LogService.info(
    `ReceiptCounter Sync: Found ${payloads.length} payloads to sync`
  );

  const currentStateCounter = ReceiptModule.receiptCounter?.value || 0;

  for (const payload of payloads) {
    try {
      // Skip and clean up stale records (value is less than current counter)
      if (payload.value < currentStateCounter) {
        LogService.info(
          `ReceiptCounter Sync: Deleting stale record ${payload._id} (value ${payload.value} < current ${currentStateCounter})`
        );
        await dexieDB.receiptCounterPayload
          .where({ _id: payload._id })
          .delete();
        continue;
      }

      await patchExactReceiptCounter({
        groupId: payload.groupId,
        reference: payload.reference,
        value: payload.value,
        status: CounterStatus.Active,
      });

      // Delete from Dexie after successful sync
      await dexieDB.receiptCounterPayload.where({ _id: payload._id }).delete();
      LogService.info(
        `ReceiptCounter Sync: Successfully synced and deleted ${payload._id}`
      );
    } catch (error) {
      LogService.info(
        `ReceiptCounter Sync: Error syncing ${payload._id}`,
        { error },
        LogLevel.Error
      );
      // Continue with next payload — don't let one failure block others
    }
  }
}
